import { cardBrandData } from "../../utils/cardBrandData";
import CardBrandTrieNode from "./CardBrandTrieNode";

/**
 * A Prefix tree for determining the card brand based on a card number
 */
export default class CardBrandTrie {
  root: CardBrandTrieNode;

  constructor() {
    this.root = new CardBrandTrieNode();
  }

  /**
   * Inserts a card brand into the Trie based on the provided prefix.
   *
   * This method traverses the Trie character by character, creating nodes
   * as necessary to represent the prefix. At the end of the prefix, it
   * associates the specified card brand information with the last node.
   *
   * @param prefix - The prefix of the card number (e.g., "4" for Visa).
   * @param cardInfo - The card information associated with the prefix.
   */
  insert(prefix: string, cardInfo: (typeof cardBrandData)[number]) {
    let node = this.root;

    // eslint is not able to perform linting for iterators/generators
    // eslint-disable-next-line no-restricted-syntax
    for (const char of prefix) {
      if (!node.children[char]) {
        node.children[char] = new CardBrandTrieNode();
      }
      node = node.children[char];
    }
    node.cardInfo = cardInfo;
  }

  /**
   * Searches for the card information associated with the given card number.
   *
   * This method traverses the Trie using the characters of the card number.
   * It returns the card information associated with the longest matching prefix.
   * If no matching prefix is found, it returns null.
   *
   * @param cardNumber - The card number being checked (e.g., "4111111111111111").
   * @returns The card information associated with the longest matching prefix,
   *          or null if no matching prefix is found.
   */
  search(cardNumber: string) {
    let node = this.root;
    let lastMatchingNode = null;

    // eslint is not able to perform linting for iterators/generators
    // eslint-disable-next-line no-restricted-syntax
    for (const char of cardNumber) {
      if (node.children[char]) {
        node = node.children[char];
        // Update the last matching node if it contains cardInfo
        if (node.cardInfo) {
          lastMatchingNode = node;
        }
      } else {
        break;
      }
    }

    // Return the cardInfo from the last matching node
    return lastMatchingNode ? lastMatchingNode.cardInfo : null;
  }
}
